import React, {useEffect, useState, useRef} from 'react';
import styles from './ScoreMarker.module.css';

type ScoreMarkerProps = {
  start: number;
  end: number;
  score: number;
  style?: React.CSSProperties;
};

const UpTriangleSvg = () => (
  <svg width={6} height={3} viewBox="0 0 6 3" fill="none" style={{display: 'block'}}>
    <path d="M3 0L6 3H0L3 0z" fill="var(--color-web-purple2)" />
  </svg>
);

const DownTriangleSvg = () => (
  <svg width={6} height={3} viewBox="0 0 6 3" fill="none" style={{display: 'block'}}>
    <path d="M3 3L0 0h6L3 3z" fill="var(--color-web-purple2)" />
  </svg>
);

const ScoreMarker: React.FC<ScoreMarkerProps> = React.memo(({start, end, score, style}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    };

    // Use setTimeout to ensure layout is complete
    const timeoutId = setTimeout(updateWidth, 0);

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  const marks = Array.from({length: end - start + 1}, (_, a) => a + start).reverse();

  const internalPadding = 13;
  const triangleWidth = 6;
  const markerLineWidth = 2;

  const scaleWidth = containerWidth > 0 ? containerWidth : 0;
  const paddingOffset = internalPadding / 2;
  const availableWidth = Math.max(scaleWidth - internalPadding, 0);
  const itemWidth = marks.length > 0 ? availableWidth / marks.length : 0;

  const rawIndex = marks.indexOf(score);
  const progress = end !== start ? (score - start) / (end - start) : 0;
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const approximateIndex = (marks.length - 1) * (1 - clampedProgress);
  const resolvedIndex = rawIndex !== -1 ? rawIndex : approximateIndex;
  const index = Math.max(0, Math.min(marks.length - 1, resolvedIndex));

  const markCenterX = paddingOffset + itemWidth * index + itemWidth / 2;
  const markerLeft = markCenterX - triangleWidth / 2;

  return (
    <div ref={containerRef} className={styles.container} style={{...style}}>
      <div
        style={{
          position: 'absolute',
          left: markerLeft,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div className={styles.triangle}>
          <DownTriangleSvg />
        </div>
        <div key="marker" className={styles.line} style={{width: markerLineWidth, height: 32}} />
        <div className={styles.triangle}>
          <UpTriangleSvg />
        </div>
      </div>
    </div>
  );
});

export {ScoreMarker};
